import { useEffect } from 'react';

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

const useFocusTrap = (containerRef, isActive, options = {}) => {
  const { triggerRef, onEscape, autoFocus = false, focusSelector, isTriggerFocusable = false } = options;

  useEffect(() => {
    if (!isActive || !containerRef.current) return;
    if (!autoFocus && !focusSelector) return;

    const timeoutId = setTimeout(() => {
      let elementToFocus;

      if (focusSelector) {
        elementToFocus = containerRef.current?.querySelector(focusSelector);
      } else if (autoFocus) {
        const focusableElements = containerRef.current?.querySelectorAll(FOCUSABLE_SELECTOR);
        elementToFocus = focusableElements?.[0];
      }

      elementToFocus?.focus();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [isActive, autoFocus, focusSelector, containerRef]);

  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = event => {
      if (event.key === 'Escape' && onEscape) {
        event.preventDefault();
        event.stopPropagation();
        onEscape();
        triggerRef?.current?.focus();
        return;
      }

      if (event.key === 'Tab') {
        const containerElements = Array.from(containerRef.current?.querySelectorAll(FOCUSABLE_SELECTOR) || []);
        const focusableElements =
          isTriggerFocusable && triggerRef?.current ? [triggerRef.current, ...containerElements] : containerElements;

        if (!focusableElements.length) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.body.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive, containerRef, triggerRef, onEscape, isTriggerFocusable]);
};

export default useFocusTrap;
